import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AttendanceCellBadge } from "@/components/attendance/attendance-cell";
import type {
  AttendanceCell,
  Category,
  OverrideStatus,
  SessionKey,
} from "@/lib/types";
import { CATEGORY_LABELS } from "@/lib/types";

const STATUS_OPTIONS: { value: OverrideStatus; label: string }[] = [
  { value: "P", label: "Present (P)" },
  { value: "L", label: "Late (L)" },
  { value: "A", label: "Absent (A)" },
  { value: "O", label: "Official leave (O)" },
  { value: "S", label: "Sick (S)" },
  { value: "TP", label: "Taken permission (TP)" },
  { value: "MS", label: "Mahamantras submitted (MS)" },
  { value: "FS", label: "Fine submitted (FS)" },
];

interface OverrideDialogProps {
  open: boolean;
  onClose: () => void;
  personName: string;
  personId: string;
  date: string;
  category: Category;
  sessionKey: SessionKey;
  sessionLabel: string;
  cell: AttendanceCell | null;
  onSave: (data: {
    status: OverrideStatus;
    late_minutes?: number;
    reason: string;
  }) => void;
  onClear: () => void;
  isSaving?: boolean;
}

export function OverrideDialog({
  open,
  onClose,
  personName,
  date,
  category,
  sessionLabel,
  cell,
  onSave,
  onClear,
  isSaving,
}: OverrideDialogProps) {
  const [status, setStatus] = useState<OverrideStatus>("P");
  const [lateMinutes, setLateMinutes] = useState("15");
  const [reason, setReason] = useState("");

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className="w-full max-w-md rounded-lg border bg-white p-5 shadow-lg"
        role="dialog"
        aria-modal="true"
      >
        <h3 className="text-lg font-semibold">Manual override</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {personName} · {CATEGORY_LABELS[category]} · {sessionLabel} · {date}
        </p>

        <div className="my-4 flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Current:</span>
          <AttendanceCellBadge cell={cell} />
          {cell?.is_overridden && (
            <span className="text-xs text-amber-700">(manual)</span>
          )}
        </div>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSave({
              status,
              late_minutes:
                status === "L" ? Number(lateMinutes) || 1 : undefined,
              reason: reason.trim(),
            });
          }}
        >
          <div className="space-y-2">
            <Label>New status</Label>
            <Select
              value={status}
              onValueChange={(v) => setStatus(v as OverrideStatus)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {status === "L" && (
            <div className="space-y-2">
              <Label>Late minutes</Label>
              <Input
                type="number"
                min={1}
                value={lateMinutes}
                onChange={(e) => setLateMinutes(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Reason (required)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Device error, coordinator verified present"
              required
            />
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button type="submit" disabled={isSaving || !reason.trim()}>
              {isSaving ? "Saving…" : "Save override"}
            </Button>
            {cell?.is_overridden && (
              <Button
                type="button"
                variant="outline"
                disabled={isSaving}
                onClick={onClear}
              >
                Clear &amp; recompute
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
